import type { QueryClient, QueryKey } from '@tanstack/react-query'
import type { Chat, Topic, EngagedTopic } from './apiModels'

export type ListStateChange = { settle: () => void; rollback: () => void }

// Keep pending local actions visible even when a background read finishes.
// Each rollback owns only its fields on its row, never a whole list snapshot.
function patchRows<T extends { id: string }>(
  qc: QueryClient,
  queryKey: QueryKey,
  matches: (row: T) => boolean,
  fields: (row: T) => Partial<T>,
  single = false,
): ListStateChange {
  let previous: T | undefined
  let applied: Partial<T> | undefined
  let updating = false
  const patch = (row: T): T => {
    if (!matches(row)) return row
    previous ??= row
    applied = fields(row)
    for (const key in applied) {
      if (!Object.is(row[key], applied[key])) return { ...row, ...applied }
    }
    return row
  }
  const update = (transform: (row: T) => T) => {
    updating = true
    try {
      if (single) {
        const row = qc.getQueryData<T>(queryKey)
        const next = row && transform(row)
        if (next !== row) qc.setQueryData<T>(queryKey, next)
      } else {
        const rows = qc.getQueryData<T[]>(queryKey)
        const next = rows?.map(transform)
        if (rows && next?.some((row, index) => row !== rows[index])) qc.setQueryData<T[]>(queryKey, next)
      }
    } finally {
      updating = false
    }
  }
  update(patch)
  const unsubscribe = qc.getQueryCache().subscribe((event) => {
    if (!updating && event.type === 'updated' && event.action.type === 'success'
      && JSON.stringify(event.query.queryKey) === JSON.stringify(queryKey)) update(patch)
  })
  return {
    settle: unsubscribe,
    rollback: () => {
      unsubscribe()
      update((row) => {
        if (!matches(row) || !previous || !applied) return row
        const restored = { ...row }
        for (const key in applied) {
          if (Object.is(row[key], applied[key])) restored[key] = previous[key]
        }
        return restored
      })
    },
  }
}

const pendingScopes = new WeakMap<QueryClient, Map<string, ListStateChange>>()

export function stageListState(
  qc: QueryClient,
  chatId: string,
  topicId: string | null,
  action: { kind: 'send' } | { kind: 'snooze'; wakeAt: number | null }
    | { kind: 'closed'; closedAt: number | null }
    | { kind: 'title'; title: string }
    | { kind: 'pin'; pinned: boolean },
): ListStateChange {
  const scopes = pendingScopes.get(qc) ?? new Map<string, ListStateChange>()
  pendingScopes.set(qc, scopes)
  const scopeKey = JSON.stringify([chatId, topicId, action.kind])
  scopes.get(scopeKey)?.settle()
  const changes: ListStateChange[] = []
  if (topicId && action.kind !== 'pin') {
    const topicFields = (topic: Topic): Partial<Topic> => action.kind === 'closed'
      ? { closed: action.closedAt !== null, closed_at: action.closedAt }
      : action.kind === 'title'
      ? { title: action.title }
      : action.kind === 'snooze'
      ? { snoozed_until: action.wakeAt, is_snoozed: (action.wakeAt ?? 0) > Date.now() / 1000 }
      : { status: topic.status === 'working' ? 'working' : 'queued' }
    const keys = qc.getQueriesData<Topic[]>({ queryKey: ['topics', chatId] }).map(([key]) => key)
    if (keys.length === 0) keys.push(['topics', chatId])
    for (const key of keys) changes.push(patchRows<Topic>(qc, key, (row) => row.id === topicId, topicFields))
    if (action.kind !== 'closed') changes.push(patchRows<EngagedTopic>(qc, ['engaged-topics'],
      (row) => row.chat_id === chatId && row.id === topicId,
      (row) => action.kind === 'title'
        ? { title: action.title }
        : action.kind === 'snooze'
        ? { snoozed_until: action.wakeAt }
        : { queued: Math.max(1, row.queued), awaiting_answer: false }))
  } else if (!topicId && action.kind !== 'closed' && action.kind !== 'title') {
    const chatFields = (chat: Chat): Partial<Chat> => action.kind === 'pin'
      ? { pinned: action.pinned }
      : action.kind === 'snooze'
      ? { snoozed_until: action.wakeAt, is_snoozed: (action.wakeAt ?? 0) > Date.now() / 1000 }
      : { queue_depth: Math.max(1, chat.queue_depth ?? 0), last_message_direction: 'inbound', awaiting_answer: false }
    changes.push(patchRows<Chat>(qc, ['chats'], (row) => row.id === chatId, chatFields))
    changes.push(patchRows<Chat>(qc, ['chat', chatId], (row) => row.id === chatId, chatFields, true))
  }
  const result: ListStateChange = {
    settle: () => {
      changes.forEach((change) => change.settle())
      if (scopes.get(scopeKey) === result) scopes.delete(scopeKey)
    },
    rollback: () => {
      if (scopes.get(scopeKey) !== result) return
      result.settle()
      changes.forEach((change) => change.rollback())
    },
  }
  scopes.set(scopeKey, result)
  return result
}
